/**
 * statusHistory sagas (VE-26730): watcher-based recording of redaction-status
 * transitions into the `redact-tdo-status-history` SDO.
 *
 * Registered globally (upload/trim seeding fires outside editor routes). Like
 * the global saga registry itself, the watchers reference redact-app action
 * types from common-app — the established cross-app pattern (see filePicker,
 * trim).
 *
 * Recording rules (business-rules.md):
 * - BR-3: rows are written only after the underlying persistence succeeds.
 *   The video path must NOT watch FETCH_STATUS_CHANGE_SUCCESS alone — that
 *   action also fires on every save, on empty-status seeding, and on external
 *   add (updateStatusOnDetails) — so it watches STATUS_CHANGE and races the
 *   matching success/failure.
 * - BR-3a: STATUS_CHANGE with meta.isLocalRollback is an optimistic rollback,
 *   never recorded. BR-3b: SET_PENDING_LOCKED_STATUS means the change was NOT
 *   persisted now; the later replay records on its own success.
 * - BR-4: programmatic transitions carry explicit markers
 *   (meta.statusHistorySystem / payload.isSystemTransition) → isSystem: true.
 * - BR-2: writes are best-effort; the writer never throws into these flows.
 *
 * Note transport: correction notes are user-authored case text and reach the
 * writer through redux only — STATUS_CHANGE meta (video) and the request payload
 * (documents) here; the folder view dispatches RECORD_STATUS_CHANGE directly
 * from performUpdateRedactionStatus, which owns both the request and its
 * terminal action (a watcher there could not correlate the two — see that saga).
 * No path puts a note into a GraphQL variables object.
 *
 * Read funnel (SECURITY-11): onFetchLatest/onFetchHistory drop any fetch for a
 * TDO not marked read-verified by a server-acknowledged read (see
 * MARK_TDO_READ_VERIFIED) — the SDO read is schema-level authorized, so this
 * funnel is the client-side access control for note text and actor emails.
 */
import {
  all,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
} from 'typed-redux-saga/macro';

import { keepAlive } from '@common/shared/keepSagasAlive';
import { takeLatestPerKey } from '@common/shared/takeLatestPerKey';
import { selectRolePermissions } from '@common/user-permissions/selectors';
import {
  FETCH_STATUS_CHANGE_FAILURE,
  FETCH_STATUS_CHANGE_SUCCESS,
  IN_REDACTION_TAG_KEY,
  SET_PENDING_LOCKED_STATUS,
  STATUS_CHANGE,
  getInitialWellKnownFileRedactionUpsertFields,
  selectTdo,
} from '@common-modules/mediaDetails';
import { CREATE_TDO_FOR_TRIM_VIDEO_SUCCESS } from '@common-modules/trim';
import {
  SET_REDACTION_STATUS_FAILURE,
  SET_REDACTION_STATUS_REQUEST,
  SET_REDACTION_STATUS_SUCCESS,
} from '@redact-modules/documents/actions';
import { selectSession } from '@redact-modules/documents/selectors';
import { CREATE_TDO_SUCCESS } from '@redact-modules/uploads/actions';
import { selectRedactionStatusSettings } from '@redact-modules/redactionStatuses/selectors';

import {
  FETCH_LATEST_STATUS_ROW,
  CLEAR_STATUS_HISTORY,
  FETCH_STATUS_HISTORY,
  FETCH_STATUS_HISTORY_FAILURE,
  RECORD_STATUS_CHANGE,
  actionRecordStatusHistory,
} from '../actions';
import {
  deriveStatusFieldsFromSelection,
  deriveStatusFieldsFromTag,
} from '../models';
import {
  clearStatusHistory,
  fetchLatestStatusRow,
  fetchStatusHistoryPage,
  writeStatusHistoryRow,
} from '../services';
import { selectIsTdoReadVerified, selectStatusHistoryGate } from '../selectors';
import type { StatusHistoryNoteOutcome } from '../models';

interface StatusChangeMeta {
  readonly isLocalRollback?: boolean;
  readonly statusHistorySystem?: boolean;
  readonly statusHistoryNote?: string;
  /** Reply to the note this transition leaves behind (VE-26730). */
  readonly statusHistoryResponse?: string;
  readonly statusHistoryResponseType?: StatusHistoryNoteOutcome;
}

type StatusChangeAction = ReturnType<typeof STATUS_CHANGE> & {
  readonly meta?: StatusChangeMeta;
};

interface TagLike {
  readonly value?: unknown;
  readonly redactionStatus?: unknown;
  readonly redactionStatusKey?: unknown;
}

const findInRedactionTag = (tags: unknown): TagLike | undefined => {
  if (!Array.isArray(tags)) {
    return undefined;
  }
  return (tags as TagLike[]).find((t) => t?.value === IN_REDACTION_TAG_KEY);
};

/** Cheap pre-check; the writer re-resolves the gate authoritatively (BR-1). */
function* isGateKnownDisabled() {
  const gate = yield* select(selectStatusHistoryGate);
  return gate === 'disabled';
}

// ---------------------------------------------------------------------------
// Flow 1 — video / AV / image editor
// ---------------------------------------------------------------------------

export function* onVideoStatusChange(action: StatusChangeAction) {
  if (action.meta?.isLocalRollback) {
    return; // BR-3a
  }
  if (yield* isGateKnownDisabled()) {
    return;
  }
  const tdo = yield* select(selectTdo);
  const tdoId = tdo?.id;
  if (!tdoId) {
    return;
  }
  const outcome = yield* race({
    success: take(
      (a: { type: string; payload?: { updateTDO?: { id?: string } } }) =>
        a.type === FETCH_STATUS_CHANGE_SUCCESS.type &&
        a.payload?.updateTDO?.id === tdoId
    ),
    failure: take(
      (a: { type: string; meta?: { variables?: { tdoId?: string } } }) =>
        a.type === FETCH_STATUS_CHANGE_FAILURE.type &&
        a.meta?.variables?.tdoId === tdoId
    ),
    // The change was parked, not persisted — the replay records later (BR-3b).
    pendingLock: take(SET_PENDING_LOCKED_STATUS.type),
    // A newer change replaces this one (the origin handler is takeLatest).
    superseded: take(
      (a: { type: string; meta?: StatusChangeMeta }) =>
        a.type === STATUS_CHANGE.type && !a.meta?.isLocalRollback
    ),
  });
  if (!outcome.success) {
    return;
  }
  const successAction = outcome.success as {
    payload?: { updateTDO?: { details?: { tags?: unknown } } };
  };
  // The dispatched payload is authoritative, the echoed tag is the fallback.
  // FETCH_STATUS_CHANGE_SUCCESS has several origins for one TDO (see the header):
  // a concurrent save (updateStatusOnDetails) sends the tags as they were BEFORE
  // this transition, so if its response wins the race, trusting the tag would
  // record the OLD status and lose the transition the user actually made. The
  // tag still covers programmatic dispatches whose payload carries no status.
  const tag = findInRedactionTag(
    successAction.payload?.updateTDO?.details?.tags
  );
  const fields =
    deriveStatusFieldsFromSelection(action.payload ?? {}) ||
    (tag ? deriveStatusFieldsFromTag(tag) : null);
  if (!fields) {
    return;
  }
  yield* put(
    actionRecordStatusHistory({
      tdoId,
      ...fields,
      isSystem: Boolean(action.meta?.statusHistorySystem),
      note: action.meta?.statusHistoryNote,
      response: action.meta?.statusHistoryResponse,
      responseType: action.meta?.statusHistoryResponseType,
    })
  );
}

function* watchVideoStatusChanges() {
  yield* takeEvery(STATUS_CHANGE.type, onVideoStatusChange);
}

// ---------------------------------------------------------------------------
// Flow 2 — document editor
// ---------------------------------------------------------------------------

export function* onDocumentStatusRequest(
  action: ReturnType<typeof SET_REDACTION_STATUS_REQUEST>
) {
  if (yield* isGateKnownDisabled()) {
    return;
  }
  /**
   * THE DOCUMENT THE REQUEST NAMES, else the one on screen — the same
   * defaulting `onSetRedactionStatusRequest` applies, so the history entry
   * and the mutation describe the same file.
   *
   * Save All's To Do → Draft promotions name a SUSPENDED document. Read off
   * the open session, this correlated on the wrong tdoId (so the race below
   * never resolved) and, when it did resolve, filed another file's
   * transition against the document that happened to be on screen.
   */
  const session = yield* select(selectSession);
  const tdoId = action.payload.tdoId ?? session?.tdoId;
  if (!tdoId) {
    return;
  }
  // Correlated on the terminal action's own tdoId (same reason as Flow 1 and
  // the folder view): a type-only match would let a second producer or a
  // re-entrant document session vouch for the wrong request's persistence.
  const outcome = yield* race({
    success: take(
      (a: { type: string; payload?: { tdoId?: string } }) =>
        a.type === SET_REDACTION_STATUS_SUCCESS.type &&
        a.payload?.tdoId === String(tdoId)
    ),
    failure: take(
      (a: { type: string; payload?: { tdoId?: string } }) =>
        a.type === SET_REDACTION_STATUS_FAILURE.type &&
        a.payload?.tdoId === String(tdoId)
    ),
  });
  if (!outcome.success) {
    return;
  }
  const successAction = outcome.success as ReturnType<
    typeof SET_REDACTION_STATUS_SUCCESS
  >;
  const fields = deriveStatusFieldsFromSelection(successAction.payload ?? {});
  if (!fields) {
    return;
  }
  const requestPayload = action.payload as {
    readonly note?: string;
    readonly response?: string;
    readonly responseType?: StatusHistoryNoteOutcome;
    readonly isSystemTransition?: boolean;
  };
  yield* put(
    actionRecordStatusHistory({
      tdoId: String(tdoId),
      ...fields,
      isSystem: Boolean(requestPayload?.isSystemTransition),
      note: requestPayload?.note,
      response: requestPayload?.response,
      responseType: requestPayload?.responseType,
    })
  );
}

function* watchDocumentStatusChanges() {
  /**
   * ONE TASK PER DOCUMENT — the same concurrency the mutation itself keeps,
   * which is the point.
   *
   * It was `takeLatest`, matching a mutation saga that was also latest-wins
   * outright. That saga moved to one task per DOCUMENT (Save All dispatches
   * a To Do → Draft promotion for each file it persisted), and mirroring the
   * old mode here recorded one history entry for a press that moved five —
   * the four it dropped being the four that happened first.
   *
   * `takeEvery` fixed that and bought a quieter defect: redux-saga's channel
   * is multicast, so two waiters for the SAME document both sit on the same
   * matcher in the race below. Change a status, change it again before the
   * first round trip settles, and the second mutation's single success
   * resolves BOTH — two rows for one transition, the older one filing the
   * first request's note against the second request's status. `takeLatest`
   * had cancelled that waiter.
   *
   * Keyed per document, both are right: a superseded change leaves no row,
   * and five documents leave five. Runs for different documents cannot take
   * each other's terminal action either, since each correlates on its own
   * tdoId (see the race below).
   */
  yield* takeLatestPerKey(
    SET_REDACTION_STATUS_REQUEST.type,
    function* keyOfRequest(action: {
      readonly payload?: { readonly tdoId?: string };
    }) {
      // The same defaulting `onDocumentStatusRequest` applies below, so the
      // key names the document the row will actually be filed against.
      const session = yield* select(selectSession);
      return String(action.payload?.tdoId ?? session?.tdoId ?? '') || null;
    },
    onDocumentStatusRequest
  );
}

// ---------------------------------------------------------------------------
// Flow 3 — seeding at TDO creation (BR-7a/7b); external add seeds via
// mainPage's absent-SDO branch dispatching RECORD_STATUS_CHANGE (BR-7c).
// The folder view has no watcher here — see the note-transport note above.
// ---------------------------------------------------------------------------

function* seedInitialStatus(tdoId: string) {
  if (yield* isGateKnownDisabled()) {
    return;
  }
  const statusSettings = yield* select(selectRedactionStatusSettings);
  const initialFields =
    getInitialWellKnownFileRedactionUpsertFields(statusSettings);
  yield* put(
    actionRecordStatusHistory({
      tdoId,
      statusLabel: initialFields.statusLabel,
      statusKey: initialFields.statusKey,
      isCustomStatus: initialFields.isCustomStatus,
      isSystem: false,
      // This row IS the file's first status, so the history it opens covers
      // the file's whole life (BR-7a/7b).
      origin: 'created',
    })
  );
}

export function* onUploadCreateTdoSuccess(
  action: ReturnType<typeof CREATE_TDO_SUCCESS>
) {
  // CREATE_TDO_SUCCESS also fires for failed uploads (meta.variables.error).
  const meta = action.meta as {
    readonly variables?: { readonly error?: string };
  };
  if (meta?.variables?.error) {
    return;
  }
  const tdoId = action.payload?.createTDO?.id;
  if (!tdoId) {
    return;
  }
  // Deliberately no parentFolderId skip — folder uploads seed too (BR-7a).
  yield* seedInitialStatus(String(tdoId));
}

export function* onTrimCreateTdoSuccess(action: {
  readonly type: string;
  readonly payload?: { readonly createTDOWithAsset?: { readonly id?: string } };
}) {
  const tdoId = action.payload?.createTDOWithAsset?.id;
  if (!tdoId) {
    return;
  }
  yield* seedInitialStatus(String(tdoId));
}

function* watchTdoCreationSuccesses() {
  yield* all([
    fork(function* () {
      yield* takeEvery(CREATE_TDO_SUCCESS.type, onUploadCreateTdoSuccess);
    }),
    fork(function* () {
      yield* takeEvery(
        CREATE_TDO_FOR_TRIM_VIDEO_SUCCESS,
        onTrimCreateTdoSuccess
      );
    }),
  ]);
}

// ---------------------------------------------------------------------------
// Funnel + reads
// ---------------------------------------------------------------------------

function* onRecordStatusChange(
  action: ReturnType<typeof RECORD_STATUS_CHANGE>
) {
  // The service is the authoritative gate check and never throws (BR-1, BR-2).
  yield* put(writeStatusHistoryRow(action.payload) as never);
}

function* watchRecordStatusChange() {
  yield* takeEvery(RECORD_STATUS_CHANGE.type, onRecordStatusChange);
}

// Both reads are demand-triggered on every editor open, so they honor the same
// cheap pre-check as the write flows: without it, an unprovisioned environment
// re-attempts the registry lookup on every single file open.
//
// AUTHORIZATION FUNNEL (VE-26730 / SECURITY-11): history rows carry
// user-authored correction notes and the acting user's email, and the SDO read
// is authorized at schema level, not per TDO — `filter: { tdoId }` is a data
// column match, not an ACL. So no read leaves this funnel unless the TDO has
// been marked read-verified by a SERVER-acknowledged fetch of that TDO for this
// caller (MARK_TDO_READ_VERIFIED — dispatched on FETCH_TDO_SUCCESS for
// video/AV/image and on the document editor's TDO media-info fetch success).
// Route-level "the page opened" actions are NOT proof: the document route
// dispatches OPEN_DOCUMENT_SUCCESS with the client-echoed tdoId even when the
// authorization-bearing fetch failed. Gating here (rather than at each
// dispatcher) also covers every future dispatch surface.
/**
 * The TDO open in an editor right now, from whichever editor owns the route:
 * the media slice for video/AV/image, the document session for PDFs. `null`
 * everywhere else — the folder view, settings, trash.
 *
 * Exists for the clear gate: the role verdict it consults describes THIS file
 * and no other, so the gate has to know which file that is.
 */
function* selectOpenEditorTdoId() {
  const mediaTdo = yield* select(selectTdo);
  const mediaTdoId = (mediaTdo as { id?: string | number } | undefined)?.id;
  if (mediaTdoId) {
    return String(mediaTdoId);
  }
  const session = yield* select(selectSession);
  const documentTdoId = session?.tdoId as string | number | undefined;
  return documentTdoId ? String(documentTdoId) : null;
}

function* isTdoReadVerified(tdoId: string) {
  return yield* select((state) => selectIsTdoReadVerified(state, tdoId));
}

export function* onFetchLatest(
  action: ReturnType<typeof FETCH_LATEST_STATUS_ROW>
) {
  if (yield* isGateKnownDisabled()) {
    return;
  }
  if (!(yield* isTdoReadVerified(action.payload.tdoId))) {
    // Fixed strings only — never row content — and loud on purpose: a dropped
    // read here means a dispatcher fired without server-proven read access.
    console.warn('statusHistory: latest-row read dropped — TDO not verified', {
      tdoId: action.payload.tdoId,
    });
    return;
  }
  yield* put(fetchLatestStatusRow(action.payload) as never);
}

export function* onFetchHistory(
  action: ReturnType<typeof FETCH_STATUS_HISTORY>
) {
  if (yield* isGateKnownDisabled()) {
    return;
  }
  if (!(yield* isTdoReadVerified(action.payload.tdoId))) {
    console.warn('statusHistory: history read dropped — TDO not verified', {
      tdoId: action.payload.tdoId,
    });
    // The FETCH_STATUS_HISTORY reducer case set isLoading before this drop;
    // terminate it or the history modal spins forever. Fixed string — the UI
    // renders its own generic i18n error, never this message.
    yield* put(
      FETCH_STATUS_HISTORY_FAILURE({
        tdoId: action.payload.tdoId,
        message: 'read not permitted',
      })
    );
    return;
  }
  yield* put(fetchStatusHistoryPage(action.payload) as never);
}

/**
 * Clearing SOFT-deletes: every live row is re-written with `isDeleted: true`
 * and a tombstone marked `origin: 'cleared'` is appended. Nothing is removed
 * server-side, so the trail survives and un-setting the flag brings it back.
 *
 * The work lives in the `clearStatusHistory` thunk rather than here, because
 * both halves need dispatch/getState and must be awaited in order. This watcher
 * only owns the gate check and the dispatch.
 */
export function* onClearStatusHistory(
  action: ReturnType<typeof CLEAR_STATUS_HISTORY>
) {
  if (yield* isGateKnownDisabled()) {
    return;
  }
  if (!(yield* isTdoReadVerified(action.payload.tdoId))) {
    // Same funnel as the reads (SECURITY-11), and the stronger case: a clear
    // is destructive, so a TDO whose read access was never server-proven has
    // no business being cleared from this client.
    console.warn('statusHistory: clear dropped — TDO not verified', {
      tdoId: action.payload.tdoId,
    });
    return;
  }
  // The role check below is only MEANINGFUL for the file currently open in an
  // editor: selectRolePermissions derives its verdict from that file's slice —
  // its tags, its lock, its object-level permission — and off an editor route
  // every restrictive branch falls through to editorPermissions. So a clear for
  // any other file is refused outright rather than judged by a verdict that
  // belongs to something else (or to nothing).
  //
  // This is also what the folder view's `allowClear={false}` says in the UI:
  // clearing is an action you take with the file in front of you. Here it is
  // enforced rather than presented.
  const openTdoId = yield* selectOpenEditorTdoId();
  if (!openTdoId || openTdoId !== String(action.payload.tdoId)) {
    console.warn('statusHistory: clear dropped — file is not the open editor', {
      tdoId: action.payload.tdoId,
    });
    return;
  }
  // Re-checked here and not only on the button: SDO writes are authorized at
  // schema level, so nothing server-side stops a hand-dispatched
  // CLEAR_STATUS_HISTORY. This is the only destructive operation in the module,
  // and a user who cannot alter a file must not be able to hide what was done
  // to it.
  //
  // Only VIEW-ONLY is denied. A status edit lock deliberately keeps the
  // permission (statusLockChangePermissions inherits it from review mode) —
  // that lock freezes the file's redactions, not the administration of its
  // provenance.
  const permissions = yield* select(selectRolePermissions);
  if (!permissions.clearStatusHistory) {
    console.warn('statusHistory: clear dropped — role may not clear history', {
      tdoId: action.payload.tdoId,
      roleName: permissions.roleName,
    });
    return;
  }
  yield* put(clearStatusHistory(action.payload.tdoId) as never);
}

function* watchClearStatusHistory() {
  yield* takeEvery(CLEAR_STATUS_HISTORY.type, onClearStatusHistory);
}

function* watchFetches() {
  yield* all([
    fork(function* () {
      yield* takeEvery(FETCH_LATEST_STATUS_ROW.type, onFetchLatest);
    }),
    fork(function* () {
      yield* takeEvery(FETCH_STATUS_HISTORY.type, onFetchHistory);
    }),
  ]);
}

export const statusHistorySaga = keepAlive([
  watchVideoStatusChanges,
  watchDocumentStatusChanges,
  watchTdoCreationSuccesses,
  watchRecordStatusChange,
  watchFetches,
  watchClearStatusHistory,
]);
